const ALLSTATIONS = window.allData; // allData comes from stations.js
const STATIONDICT = Object.fromEntries(
    ALLSTATIONS.map((row) => {
        const id = row[3];
        const ntwk = row[5];
        return [`${id}:${ntwk}`, row];
    }),
);
const PROBINDEX = { 95: 0, 90: 0, 70: 1, 50: 2, 30: 3, 10: 4, 5: 4 };

/**
 * For a given list of forecasts (obtained from the get-data module)
 * this function filters the list to the specified period and station,
 * and creates 2 processed array ('forecastValues' and 'forecastProbabilities')
 *
 * @param {String} triplet - e.g. 10042929:MT:USGS
 * @param {String} period - e.g. APR-JUL
 * @param {Object} forecasts - list of forecasts
 * @returns
 */
export function processForecastsForStation(triplet, period, forecasts) {
    // check for forecasts
    const forecast = forecasts?.[triplet]?.[period];

    // initialize outputs
    let forecastValues = Array(5).fill(null);
    let forecastProbabilities = Array(5).fill(null);
    let hasForecastData = false;

    if (forecast) {
        for (let i = 0; i < forecast.length; i += 2) {
            const prob = forecast[i];
            const value = forecast[i + 1];
            const index = PROBINDEX?.[prob];
            if (index >= 0 && prob && value !== null && value !== undefined) {
                forecastValues[index] = value;
                forecastProbabilities[index] = prob;
                hasForecastData = true;
            }
        }
    }

    return {
        hasForecastData: hasForecastData,
        forecast: forecast,
        forecastValues: forecastValues,
        forecastProbabilities: forecastProbabilities,
    };
}

/**
 * This function returns the station triplet and
 * forecast point name for the provided network id
 * @param {String} id - network ID
 * @returns
 */
export function getStationMetadataForID(id) {
    // first try to find metadata for <id + 'USGS'>
    if (STATIONDICT.hasOwnProperty(`${id}:USGS`)) {
        const station = STATIONDICT[`${id}:USGS`];
        return { triplet: `${id}:${station[4]}:USGS`, name: station[0] };

        // Otherwise, try the first match for ID
    } else {
        const candidates = ALLSTATIONS.filter((s) => s[3] === id);
        if (candidates.length) {
            const station = candidates[0];
            return { triplet: `${id}:${station[4]}:${station[5]}`, name: station[0] };
        }
    }
    return { triplet: null, name: null };
}

/**
 * This function processes the list of observations and normals to retrieve the
 * station specific normal as well as the station min/max/observed for the given
 * triplet, period, and year.
 *
 * @param {String} triplet - e.g. 10042929:MT:USGS
 * @param {String} period  - e.g. APR-JUL
 * @param {Object} normals - normals file
 * @param {Object} observations - observed file
 * @param {number} year - specified year (e.g. 2025)
 * @returns
 */
export function processStationStats(triplet, period, normals, observations, year) {
    // normal
    const normal = normals?.[triplet]?.[period];

    // min, max, minYear, maxYear
    let min, max, minYear, maxYear, observed, numberObservations, hasDataForAnyYear;
    const obsData = observations?.[triplet]?.[period];
    if (obsData) {
        const startYear = obsData[0];
        const indexOfRequestedYear = year - startYear; //+ 1;
        const values = obsData.slice(1);
        const nonNullValues = values.filter((v) => v);
        observed = values?.[indexOfRequestedYear];
        min = Math.min(...nonNullValues);
        max = Math.max(...nonNullValues);
        minYear = startYear + values.indexOf(min);
        maxYear = startYear + values.indexOf(max);
        numberObservations = nonNullValues.length;
        hasDataForAnyYear = numberObservations > 0;
    }

    return {
        observed: observed || null,
        max: max || null,
        min: min || null,
        maxYear: maxYear || null,
        minYear: minYear || null,
        numberObservations: numberObservations || null,
        hasDataForAnyYear: hasDataForAnyYear || false,
        normal: normal || null,
    };
}

/**
 * For each station in basin, this function creates a single entry
 * per available period. An entry consists of that station/period's
 * forecast information, observation information, stats information,
 *
 *
 * @param {*} obs
 * @param {*} stats
 * @param {*} forecasts
 * @param {*} stations
 * @param {*} inputs
 * @returns
 */
export function createEntries(obs, stats, forecasts, stations, inputs) {
    // Array storing all the data to plot
    const data = [];

    // Iterate over stations and selected periods and add to data to plot
    for (const station of stations) {
        for (const period of inputs.period) {
            // Network ID, period, triplet, and name
            const entry = { id: station, period: period };
            Object.assign(entry, getStationMetadataForID(station));

            // Normals, observed, stats
            Object.assign(entry, processStationStats(entry.triplet, period, stats, obs, inputs.year));

            // Forecast data
            Object.assign(entry, processForecastsForStation(entry.triplet, period, forecasts));

            entry.hasAnyData = entry.hasForecastData || entry.hasDataForAnyYear;

            // Add percents of normal
            if (entry.normal === null || entry.normal === undefined) {
                entry.forecastValuesPctOfNormal = Array(5).fill(null);
                entry.obsPctOfNormal = null;
                entry.minPctOfNormal = null;
                entry.maxPctOfNormal = null;
                entry.normalPctOfNormal = null;
                entry.hasAnyData = false;
            } else {
                entry.forecastValuesPctOfNormal = entry.forecastValues.map((v) => {
                    if (v === null || v === undefined) {
                        return null;
                    }
                    return (v * 100) / entry.normal;
                });
                entry.obsPctOfNormal =
                    entry.observed === null || entry.observed === undefined
                        ? null
                        : (entry.observed * 100) / entry.normal;
                entry.minPctOfNormal =
                    entry.min === null || entry.min === undefined ? null : (entry.min * 100) / entry.normal;
                entry.maxPctOfNormal =
                    entry.max === null || entry.max === undefined ? null : (entry.max * 100) / entry.normal;
                entry.normalPctOfNormal = 100;
                if (
                    entry.obsPctOfNormal === null &&
                    entry.minPctOfNormal === null &&
                    entry.maxPctOfNormal === null &&
                    !entry.forecastValuesPctOfNormal.some((v) => v)
                ) {
                    entry.hasAnyData = false;
                }
            }

            if (inputs.hideNeg && entry.normal <= 0) {
                continue;
            }
            // Only include entries that have forecast data
            if (inputs.hideEmpty) {
                if (entry.hasForecastData) {
                    data.push(entry);
                }
            } else {
                if (entry.hasAnyData) {
                    data.push(entry);
                }
            }
        }
    }

    return data;
}
